import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../Redux/Store';
import { fetchPaymentMethods, requestCheckout } from '../Redux/Public/orderApi';
import { CheckoutRequestState } from '../types/order';

interface UseCheckoutRequestProps {
  activeStoreId: string;
  activeDiningTableId: string;
  /** 送信成功時に BE が返した CheckoutRequestState を通知する（useCheckoutRequestState.setState を渡す）。ADR 0068 */
  onStateChange?: (state: CheckoutRequestState) => void;
}

// 会計リクエスト（会計希望）の状態管理。
// ボタン押下で店舗の支払方法を取得してダイアログ表示 → 顧客が方法を選ぶと店員へ通知する。
export const useCheckoutRequest = ({ activeStoreId, activeDiningTableId, onStateChange }: UseCheckoutRequestProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState(false);
  const [methods, setMethods] = useState<string[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenCheckoutRequest = async () => {
    if (!activeStoreId || !activeDiningTableId) return;
    setOpen(true);
    setError(null);
    setLoadingMethods(true);
    try {
      const list = await dispatch(
        fetchPaymentMethods({ activeStoreId, activeDiningTableId })
      ).unwrap();
      setMethods(list && list.length > 0 ? list : ['現金']);
    } catch {
      // 取得失敗時も会計希望自体は送れるよう、現金のみで続行する
      setMethods(['現金']);
    } finally {
      setLoadingMethods(false);
    }
  };

  const handleSelectPaymentMethod = async (paymentMethod: string) => {
    if (!activeStoreId || !activeDiningTableId) return;
    setSubmitting(true);
    setError(null);
    try {
      const state = await dispatch(
        requestCheckout({ activeStoreId, activeDiningTableId, paymentMethod })
      ).unwrap();
      // 応答の状態で即座にバナーを REQUESTED 表示にする（旧 BE の空応答なら null のため何もしない）
      if (state) {
        onStateChange?.(state);
      }
      setOpen(false);
      setSent(true);
    } catch (err) {
      // BE の 409（受付済み）などは message をそのまま出す。それ以外は汎用文言。
      setError(
        typeof err === 'string' && err
          ? err
          : '会計リクエストの送信に失敗しました。もう一度お試しください。'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return {
    open,
    setOpen,
    methods,
    loadingMethods,
    submitting,
    sent,
    setSent,
    error,
    setError,
    handleOpenCheckoutRequest,
    handleSelectPaymentMethod,
  };
};
